import api from './api';

class AIChatService {
  constructor() {
    this.controller = null;
  }

  /**
   * Get AI chat service status
   */
  async getStatus() {
    try {
      const response = await api.get('/ai/status');
      return response.data;
    } catch (error) {
      console.error('Error getting AI chat status:', error);
      throw error;
    }
  }

  /**
   * Get case context summary for AI chat
   */
  async getCaseContext(caseId) {
    try {
      const response = await api.get(`/ai/cases/${caseId}/context`);
      return response.data;
    } catch (error) {
      console.error('Error getting case context:', error);
      throw error;
    }
  }

  /**
   * Send a message and get streaming response
   */
  async sendMessage(caseId, content, onChunk, onError, onComplete) {
    try {
      // Cancel any previous request
      if (this.controller) {
        this.controller.abort();
      }

      this.controller = new AbortController();

      // Use axios to make the initial request, then handle the streaming
      // This ensures we use the same authentication as other API calls
      const apiBaseURL = api.defaults.baseURL;

      // Make the request using fetch but be very explicit about authentication
      const response = await fetch(`${apiBaseURL}/ai/cases/${caseId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // This should include authentication cookies
        signal: this.controller.signal,
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 401) {
          // Handle authentication error the same way as api service
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return;
        }
        // Try to extract detail message from JSON error response
        let errorMessage = errorText;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed.detail) errorMessage = parsed.detail;
        } catch {
          // Not JSON, use raw text
        }
        throw new Error(errorMessage);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonData = line.slice(6);
                console.log('SSE data received:', jsonData);
                const data = JSON.parse(jsonData);
                console.log('Parsed SSE data:', data);

                if (data.type === 'content' && data.content) {
                  console.log('Calling onChunk with:', data.content);
                  onChunk(data.content);
                } else if (data.type === 'done') {
                  console.log('Stream completed');
                  onComplete();
                  return;
                } else if (data.type === 'error') {
                  console.log('Stream error:', data.error);
                  onError(new Error(data.error || 'Unknown error'));
                  return;
                }
              } catch (parseError) {
                console.warn('Failed to parse SSE data:', parseError, 'Raw line:', line);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('AI Chat Error:', error);
        onError(error);
      }
    } finally {
      this.controller = null;
    }
  }

  /**
   * Get chat conversation history
   */
  async getChatHistory(caseId) {
    try {
      const response = await api.get(`/ai/cases/${caseId}/chat/history`);
      return response.data;
    } catch (error) {
      console.error('Error getting chat history:', error);
      throw error;
    }
  }

  /**
   * Clear chat conversation history
   */
  async clearChatHistory(caseId) {
    try {
      const response = await api.delete(`/ai/cases/${caseId}/chat/history`);
      return response.data;
    } catch (error) {
      console.error('Error clearing chat history:', error);
      throw error;
    }
  }

  /**
   * Refresh case context with latest data
   */
  async refreshCaseContext(caseId) {
    try {
      const response = await api.post(`/ai/cases/${caseId}/chat/refresh-context`);
      return response.data;
    } catch (error) {
      console.error('Error refreshing case context:', error);
      throw error;
    }
  }

  /**
   * Cancel any ongoing streaming request
   */
  cancelRequest() {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }
}

const aiChatServiceInstance = new AIChatService();
export default aiChatServiceInstance;